import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { useConfigurations } from '@/features/admin/configurations/hooks'
import { CustomerPicker, type CustomerPickerOption } from '@/features/customer/components/CustomerPicker'
import { HaloUserCombobox } from '@/features/halocrm-user/HaloUserCombobox'
import { useAuthStore } from '@/features/auth/store'
import { CreateContractSchema, type CreateContractInput } from '../schema'
import { useCreateContract } from '../hooks'

type SheetProps = {
  open: boolean
  onClose: () => void
}

function ContractCreateSheet({ open, onClose }: SheetProps) {
  const navigate = useNavigate()
  const create = useCreateContract()
  const products = useConfigurations(true)
  const currentUser = useAuthStore(s => s.user)
  const [customer, setCustomer] = useState<CustomerPickerOption | null>(null)
  const [salesPersonId, setSalesPersonId] = useState<number | null>(currentUser?.id ?? null)
  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue, watch, clearErrors } = useForm<CreateContractInput>({
    resolver: zodResolver(CreateContractSchema),
    defaultValues: { hasCredit: false, configurationCode: '', ordererCustomerId: 0 },
  })
  const configurationCode = watch('configurationCode')

  useEffect(() => {
    if (open && !configurationCode && products.data && products.data.length > 0) {
      setValue('configurationCode', products.data[0].code)
    }
  }, [open, products.data, configurationCode, setValue])

  // Reset customer selection and salesPersonId when sheet closes
  useEffect(() => {
    if (!open) {
      setCustomer(null)
      setSalesPersonId(currentUser?.id ?? null)
    }
  }, [open, currentUser])

  // Sync customer picker to RHF so Zod validation sees a valid positive int
  const handleCustomerChange = (option: CustomerPickerOption | null) => {
    setCustomer(option)
    if (option) {
      setValue('ordererCustomerId', option.id, { shouldValidate: true })
      clearErrors('ordererCustomerId')
    } else {
      setValue('ordererCustomerId', 0, { shouldValidate: false })
    }
  }

  const onSubmit = (values: CreateContractInput) => {
    // ordererCustomerId is set via handleCustomerChange; Zod ensures it's positive before onSubmit fires
    create.mutate({ ...values, currency: values.currency ?? 'PLN', salesPersonId: salesPersonId ?? null }, {
      onSuccess: c => {
        onClose()
        navigate(`/contracts/${c.id}`)
      },
    })
  }

  return (
    <Dialog.Root open={open} onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-[480px] bg-white shadow-xl p-5 overflow-auto">
          <Dialog.Title className="text-base font-semibold">Nowa umowa</Dialog.Title>
          <Dialog.Description className="text-sm text-slate-600 mt-1">
            Wprowadź dane podstawowe; pola dynamiczne wypełnisz po utworzeniu.
          </Dialog.Description>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 flex flex-col gap-3 text-sm">
            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Zamawiający *</span>
              <CustomerPicker value={customer} onChange={handleCustomerChange} />
              {errors.ordererCustomerId && (
                <span className="text-red-600 text-xs">{errors.ordererCustomerId.message}</span>
              )}
            </div>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Konfiguracja umowy *</span>
              <Combobox<string>
                value={configurationCode || null}
                onChange={v => setValue('configurationCode', v ?? '')}
                options={(products.data ?? []).map(p => ({ value: p.code, label: p.labelPl }))}
                placeholder="Wybierz konfigurację..."
                disabled={!products.data}
              />
              {errors.configurationCode && <span className="text-red-600 text-xs">{errors.configurationCode.message}</span>}
            </label>

            {/* Kwota i waluta zostaly wyrzucone z create - sa realnie polami dynamicznymi
                (extras) w sekcji formularza i edytuje sie je na detalu umowy. */}

            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('hasCredit')} />
              <span>Z kredytem</span>
            </label>

            <div className="grid gap-1">
              <label className="text-xs uppercase text-slate-500">Handlowiec</label>
              <HaloUserCombobox
                value={salesPersonId}
                onChange={setSalesPersonId}
                placeholder="Wybierz handlowca (opcjonalnie)"
              />
              <p className="text-xs text-slate-400">
                Domyślnie Ty. Wybierz inną osobę gdy umowę wpisujesz w imieniu handlowca.
              </p>
            </div>

            {create.isError && <p className="text-red-600 text-xs">Nie udało się utworzyć umowy.</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>
                Anuluj
              </button>
              <button type="submit" disabled={isSubmitting} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50">
                {isSubmitting ? 'Tworzenie...' : 'Utwórz'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function useContractCreateSheet() {
  const [open, setOpen] = useState(false)
  return {
    openCreate: () => setOpen(true),
    sheet: <ContractCreateSheet open={open} onClose={() => setOpen(false)} />,
  }
}
